import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { SubscriptionUpgradeRequest, ApproveSubscriptionUpgradeRequestInput, OrganizationSubscription } from "../types";

const SUBSCRIPTION_REQUEST_FIELDS = `
  id
  organizationId
  planId
  requestedBy
  status
  requestedAt
  proposedAmount
  proposedCurrency
  requestNote
  resolvedAt
  resolvedBy
  note
  createdAt
  updatedAt
`;

interface ApproveSubscriptionUpgradeRequestResult {
  request: SubscriptionUpgradeRequest;
  subscription: OrganizationSubscription;
}

export const subscriptionUpgradeRequestApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Platform-owner inbox — pass "pending" for the actionable queue, or
    // omit for the full history of every request.
    getSubscriptionUpgradeRequests: builder.query<SubscriptionUpgradeRequest[], string | void>({
      query: (status) => ({
        body: {
          query: `
            query SubscriptionUpgradeRequests($status: String) {
              subscriptionUpgradeRequests(status: $status) {
                ${SUBSCRIPTION_REQUEST_FIELDS}
              }
            }
          `,
          variables: { status: status ?? null },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<SubscriptionUpgradeRequest[]>(res, "subscriptionUpgradeRequests"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({ type: "SubscriptionUpgradeRequest" as const, id: r.id })),
              { type: "SubscriptionUpgradeRequest" as const, id: "LIST" },
            ]
          : [{ type: "SubscriptionUpgradeRequest" as const, id: "LIST" }],
    }),

    getSubscriptionUpgradeRequestsByOrganization: builder.query<SubscriptionUpgradeRequest[], string>({
      query: (organizationId) => ({
        body: {
          query: `
            query SubscriptionUpgradeRequestsByOrganization($organizationId: String!) {
              subscriptionUpgradeRequestsByOrganization(organizationId: $organizationId) {
                ${SUBSCRIPTION_REQUEST_FIELDS}
              }
            }
          `,
          variables: { organizationId },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<SubscriptionUpgradeRequest[]>(res, "subscriptionUpgradeRequestsByOrganization"),

      providesTags: (result, _err, organizationId) =>
        result
          ? [
              ...result.map((r) => ({ type: "SubscriptionUpgradeRequest" as const, id: r.id })),
              { type: "SubscriptionUpgradeRequest" as const, id: `ORG-${organizationId}` },
            ]
          : [{ type: "SubscriptionUpgradeRequest" as const, id: `ORG-${organizationId}` }],
    }),

    approveSubscriptionUpgradeRequest: builder.mutation<
      ApproveSubscriptionUpgradeRequestResult,
      { id: string; input: ApproveSubscriptionUpgradeRequestInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation ApproveSubscriptionUpgradeRequest($id: String!, $input: ApproveSubscriptionUpgradeRequestInput!) {
              approveSubscriptionUpgradeRequest(id: $id, input: $input) {
                request { ${SUBSCRIPTION_REQUEST_FIELDS} }
                subscription { id organizationId planId status }
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<ApproveSubscriptionUpgradeRequestResult>(res, "approveSubscriptionUpgradeRequest"),

      invalidatesTags: (result) => [
        { type: "SubscriptionUpgradeRequest", id: "LIST" },
        { type: "Subscription", id: "LIST" },
        ...(result
          ? [
              { type: "SubscriptionUpgradeRequest" as const, id: `ORG-${result.request.organizationId}` },
              { type: "Subscription" as const, id: `ORG-${result.subscription.organizationId}` },
              { type: "Organization" as const, id: result.subscription.organizationId },
              { type: "Organization" as const, id: "LIST" },
            ]
          : []),
      ],
    }),

    rejectSubscriptionUpgradeRequest: builder.mutation<
      SubscriptionUpgradeRequest,
      { id: string; note?: string }
    >({
      query: ({ id, note }) => ({
        body: {
          query: `
            mutation RejectSubscriptionUpgradeRequest($id: String!, $note: String) {
              rejectSubscriptionUpgradeRequest(id: $id, note: $note) {
                ${SUBSCRIPTION_REQUEST_FIELDS}
              }
            }
          `,
          variables: { id, note: note ?? null },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<SubscriptionUpgradeRequest>(res, "rejectSubscriptionUpgradeRequest"),

      invalidatesTags: (result) => [
        { type: "SubscriptionUpgradeRequest", id: "LIST" },
        ...(result
          ? [{ type: "SubscriptionUpgradeRequest" as const, id: `ORG-${result.organizationId}` }]
          : []),
      ],
    }),

    // Withdraws a request that hasn't been reviewed yet — callable by the
    // requesting org's own owner/admin, or the platform owner.
    cancelSubscriptionUpgradeRequest: builder.mutation<SubscriptionUpgradeRequest, string>({
      query: (id) => ({
        body: {
          query: `
            mutation CancelSubscriptionUpgradeRequest($id: String!) {
              cancelSubscriptionUpgradeRequest(id: $id) {
                ${SUBSCRIPTION_REQUEST_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<SubscriptionUpgradeRequest>(res, "cancelSubscriptionUpgradeRequest"),

      invalidatesTags: (result) => [
        { type: "SubscriptionUpgradeRequest", id: "LIST" },
        ...(result
          ? [{ type: "SubscriptionUpgradeRequest" as const, id: `ORG-${result.organizationId}` }]
          : []),
      ],
    }),
  }),
});

export const {
  useGetSubscriptionUpgradeRequestsQuery,
  useGetSubscriptionUpgradeRequestsByOrganizationQuery,
  useApproveSubscriptionUpgradeRequestMutation,
  useRejectSubscriptionUpgradeRequestMutation,
  useCancelSubscriptionUpgradeRequestMutation,
} = subscriptionUpgradeRequestApi;
